import React, { useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CardPanel,
  Card,
  IconHighlight,
  Button,
  Skeleton,
} from "@ann/web-components";
import { NotesContent } from "./components";
import { ContentWrapper } from "./Notes.styled";
import ScrollableCardContent from "../ScrollableCardContent";
import NotesViewAllSlideout from "./NotesViewAllSlideout";
import NotesSlideOut from "../../../Activity/components/PreviewSlideOut/NotesSlideOut";
import { useActivitySnapshot } from "../../../../hooks/useActivitySnapshot";
import { ACTIVITY_TYPES } from "../../../../context/ActivityContext";

const Notes = () => {
  const [isViewAllOpen, setIsViewAllOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const navigate = useNavigate();
  const { contactId } = useParams();

  const { getTabProps, handleRefresh, handleCreateNote, handleDeleteNote } =
    useActivitySnapshot();

  const notesTab = getTabProps(ACTIVITY_TYPES.NOTES);
  const notesForTab = useMemo(
    () => (Array.isArray(notesTab.items) ? notesTab.items : []),
    [notesTab.items]
  );
  const totalNotes = notesForTab.length;
  const subtitle = `(${totalNotes})`;
  const displayedNotes = useMemo(() => notesForTab, [notesForTab]);
  const isInitialLoading = notesTab.loading && notesForTab.length === 0;

  const handleOpenViewAll = useCallback(() => setIsViewAllOpen(true), []);
  const handleCloseViewAll = useCallback(() => setIsViewAllOpen(false), []);

  const handleOpenCreate = useCallback(() => setIsCreateOpen(true), []);

  const handleCloseCreate = useCallback(() => {
    setIsCreateOpen(false);
  }, []);

  const handleDeleteNoteClick = useCallback(
    async (note) => {
      try {
        const noteUuid =
          note.note_uuid ||
          note.uuid ||
          note.raw?.note_uuid ||
          note.raw?.uuid ||
          note.raw?.id ||
          note.raw?.noteId ||
          note.raw?.activity_uuid ||
          null;

        if (!noteUuid) {
          console.error("[Overview Notes] Cannot delete note: note_uuid not found", note);
          return;
        }

        await handleDeleteNote(noteUuid);
      } catch (error) {
        console.error("[Overview Notes] Error deleting note:", error);
      }
    },
    [handleDeleteNote]
  );

  const handleOpenNote = useCallback((note) => {
    navigate(`/v2/ucp/${contactId}/activity/notes`);
  }, [contactId, navigate]);

  const handleNavigateToNotes = useCallback(() => {
    if (!contactId) return;
    setIsViewAllOpen(false);
    navigate(`/v2/ucp/${contactId}/activity/notes`);
  }, [contactId, navigate]);

  if (isInitialLoading) {
    return <Skeleton height="571px" width="100%" />;
  }

  return (
    <>
      <Card size="large">
        <CardPanel isExpandable={false}>
          <CardPanel.Header title="Notes" subtitle={subtitle}>
            <CardPanel.Header.LeftSlot>
              <IconHighlight
                icon="stickyNote2"
                bgColor="Alert"
                iconColor="Alert"
                size="medium"
              />
            </CardPanel.Header.LeftSlot>
          </CardPanel.Header>
          <CardPanel.Content>
            <ScrollableCardContent>
              <ContentWrapper>
                <NotesContent
                  notes={displayedNotes}
                  loading={notesTab.loading}
                  error={notesTab.error}
                  onRetry={handleRefresh}
                  onDeleteNote={handleDeleteNoteClick}
                  onOpenNote={handleOpenNote}
                />
              </ContentWrapper>
            </ScrollableCardContent>
          </CardPanel.Content>
          <CardPanel.Actions>
            <Button
              size="medium"
              type="secondary"
              label="View all"
              onClick={handleOpenViewAll}
            />
            <Button
              size="medium"
              type="primary"
              label="Create"
              onClick={handleOpenCreate}
            />
          </CardPanel.Actions>
        </CardPanel>
      </Card>

      <NotesViewAllSlideout
        open={isViewAllOpen}
        onClose={handleCloseViewAll}
        notes={notesForTab}
        loading={notesTab.loading}
        error={notesTab.error}
        onRetry={handleRefresh}
        onNavigateToNotes={handleNavigateToNotes}
        onCreateNote={handleCreateNote}
        onDeleteNote={handleDeleteNoteClick}
      />

      <NotesSlideOut
        open={isCreateOpen}
        onClose={handleCloseCreate}
        showBackButton={isViewAllOpen}
        mode="create"
        onCreateNote={handleCreateNote}
      />
    </>
  );
};

export default Notes;
